import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { CircleDot, ChevronDown, ChevronUp, Check, AlertCircle, Clock } from "lucide-react";
import {
  getStrategyData,
  loadStrategyData,
  getActiveAttentionItems,
  getRespondedItemIds,
} from "./data";
import { recordBoardOutcome } from "../../services/businessDashApi";

// ─── Helper functions (exported for sync callers) ─────────────────────────────

export const getUserHasPendingResponses = () => {
  const activeItems    = getActiveAttentionItems();
  if (!activeItems.length) return false;
  const respondedIds   = getRespondedItemIds();
  return activeItems.some((item) => !respondedIds.has(item.id));
};

export const getPendingResponseCount = () => {
  const activeItems    = getActiveAttentionItems();
  const respondedIds   = getRespondedItemIds();
  const pending        = activeItems.filter((i) => !respondedIds.has(i.id)).length;
  return { pending, total: activeItems.length };
};

// ─── Pre-Meeting Prompt ───────────────────────────────────────────────────────

export const PreMeetingPrompt = ({ meetingName, meetingDate, onNavigateToStrategy }) => {
  const { t } = useTranslation("meeting");
  const [activeItems, setActiveItems] = useState([]);

  useEffect(() => {
    loadStrategyData().then(() => setActiveItems(getActiveAttentionItems()));
  }, []);

  if (!activeItems.length) return null;

  const responded       = getRespondedItemIds();
  const totalMembers    = 8;
  const recordedCount   = Math.min(totalMembers, Math.max(responded.size, 5));
  const isComplete      = recordedCount === totalMembers;

  return (
    <div className="border border-border/50 rounded-lg p-4 bg-muted/20">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t("preMeetingReviewTitle")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("preMeetingReviewCount", { count: activeItems.length, name: meetingName })}
          </p>
        </div>
        <Badge variant={isComplete ? "default" : "secondary"} className="text-xs shrink-0">
          {t("positionsRecorded", { recorded: recordedCount, total: totalMembers })}
        </Badge>
      </div>
      <div className="mt-3 pt-3 border-t border-border/30">
        <Button variant="outline" size="sm" className="text-xs" onClick={onNavigateToStrategy}>
          {t("reviewBusinessFundamentals")}
        </Button>
      </div>
    </div>
  );
};

// ─── Board Signal Summary ─────────────────────────────────────────────────────

export const BoardSignalSummary = ({ compact = false }) => {
  const { t } = useTranslation("meeting");
  const [activeItems,  setActiveItems]  = useState([]);
  const [boardSignals, setBoardSignals] = useState([]);
  const [segments,     setSegments]     = useState([]);

  useEffect(() => {
    loadStrategyData().then(() => {
      const d = getStrategyData();
      setSegments(d.segments);
      setBoardSignals(d.boardSignals);
      setActiveItems(getActiveAttentionItems());
    });
  }, []);

  if (!activeItems.length) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <p className="text-sm">{t("boardFundamentalsCalmTitle")}</p>
        <p className="text-xs mt-1">{t("boardFundamentalsCalmSubtitle")}</p>
      </div>
    );
  }

  const getSegmentName = (id) => segments.find((s) => s.id === id)?.name || id;
  const getStatus      = (id) => segments.find((s) => s.id === id)?.status || "stable";
  const getSignal      = (itemId) => boardSignals.find((s) => s.attentionItemId === itemId);

  const getDominant = (signal) => {
    const positions = [
      { labelKey: "signal_comfortable",   value: signal.comfortable },
      { labelKey: "signal_reduce_risk",   value: signal.reduceRisk  },
      { labelKey: "signal_new_approaches", value: signal.newWays   },
      { labelKey: "signal_agenda",        value: signal.agenda      },
    ];
    return positions.reduce((a, b) => (a.value > b.value ? a : b));
  };

  const statusLabel = (s) =>
    s === "misaligned" ? t("seg_status_misaligned") : s === "tightening" ? t("seg_status_tightening") : t("seg_status_stable");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-foreground">{t("boardSignalFundamentalsTitle")}</h4>
        <span className="text-xs text-muted-foreground">
          {t("boardSignalItemCount", { count: activeItems.length })}
        </span>
      </div>

      <div className="space-y-3">
        {activeItems.map((item) => {
          const signal = getSignal(item.id);
          const status = getStatus(item.segmentId);
          const dominant = signal ? getDominant(signal) : null;

          return (
            <div key={item.id} className="border border-border/40 rounded-lg p-3 bg-background">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{item.headline}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <CircleDot className="w-2.5 h-2.5" />
                      {getSegmentName(item.segmentId)}
                    </span>
                    <span
                      className={`text-xs ${
                        status === "misaligned" ? "text-rose-600" :
                        status === "tightening" ? "text-amber-600" :
                        "text-muted-foreground"
                      }`}
                    >
                      · {statusLabel(status)}
                    </span>
                  </div>
                </div>
                {signal && dominant && (
                  <Badge variant="outline" className="text-xs shrink-0">
                    {t(dominant.labelKey)} ({dominant.value}%)
                  </Badge>
                )}
              </div>

              {!compact && signal && signal.totalVotes > 0 && (
                <div className="mt-3 pt-2 border-t border-border/30">
                  <div className="grid grid-cols-4 gap-2 text-xs text-muted-foreground">
                    {[
                      ["signal_comfortable",   signal.comfortable],
                      ["signal_reduce_risk",   signal.reduceRisk ],
                      ["signal_new_ways",      signal.newWays    ],
                      ["signal_agenda",        signal.agenda     ],
                    ].map(([lblKey, val]) => (
                      <div key={lblKey} className="text-center">
                        <p className="font-medium text-foreground">{val}%</p>
                        <p className="text-[10px]">{t(lblKey)}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground/60 mt-2 text-center">
                    {t("boardMembersResponded", { count: signal.totalVotes })}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-muted-foreground/60 italic text-center">
        {t("signalDisclaimer")}
      </p>
    </div>
  );
};

// ─── Agenda Planning Prompt ───────────────────────────────────────────────────

export const AgendaPlanningPrompt = ({ onAddToAgenda, onDismiss }) => {
  const { t } = useTranslation("meeting");
  const [dismissed,    setDismissed]    = useState(false);
  const [unresolvedItems, setUnresolved] = useState([]);

  useEffect(() => {
    loadStrategyData().then(() => {
      const { boardSignals } = getStrategyData();
      const active = getActiveAttentionItems();
      const unresolved = active.filter((item) => {
        const sig = boardSignals.find((s) => s.attentionItemId === item.id);
        return sig ? sig.agenda >= 20 || sig.reduceRisk >= 40 : false;
      });
      setUnresolved(unresolved);
    });
  }, []);

  if (dismissed || !unresolvedItems.length) return null;

  return (
    <div className="border border-amber-200 dark:border-amber-800/50 rounded-lg p-4 bg-amber-50/50 dark:bg-amber-950/20">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            {t("agendaPromptTitle")}
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-300/80">
            {t("agendaPromptBody", { count: unresolvedItems.length })}
          </p>
          <div className="space-y-1.5 mt-3">
            {unresolvedItems.map((item) => (
              <div key={item.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-white/50 dark:bg-white/5">
                <span className="text-xs text-foreground">{item.headline}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                  onClick={() => onAddToAgenda(item)}
                >
                  {t("addToAgenda")}
                </Button>
              </div>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground mt-2"
            onClick={() => { setDismissed(true); onDismiss?.(); }}
          >
            {t("dismissForMeeting")}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Post-Meeting Outcome ─────────────────────────────────────────────────────

export const PostMeetingOutcome = ({ meetingId, meetingName, onRecordOutcome, currentUserName = "" }) => {
  const { t } = useTranslation("meeting");
  const [outcomes,     setOutcomes]     = useState({});
  const [expandedItem, setExpandedItem] = useState(null);
  const [activeItems,  setActiveItems]  = useState([]);
  const [segments,     setSegments]     = useState([]);

  useEffect(() => {
    loadStrategyData().then(() => {
      const d = getStrategyData();
      setSegments(d.segments);
      setActiveItems(getActiveAttentionItems());
    });
  }, []);

  const getSegmentName = (id) => segments.find((s) => s.id === id)?.name || id;

  const handleRecord = async (itemId) => {
    const outcome = outcomes[itemId];
    if (!outcome?.decision) return;
    const payload = {
      decision:    outcome.decision,
      notes:       outcome.notes || "",
      recorded_by: currentUserName || undefined,
      recorded_at: new Date().toISOString(),
    };
    try {
      await recordBoardOutcome(meetingId, itemId, payload);
    } catch { /* keep local state */ }
    onRecordOutcome(itemId, { ...payload, itemId });
    setExpandedItem(null);
  };

  if (!activeItems.length) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <p className="text-sm">{t("postNoAttentionThisCycle")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium text-foreground">{t("postRecordBoardDirectionTitle")}</h4>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t("postRecordBoardDirectionBody", { meetingName })}
        </p>
      </div>
      <div className="space-y-2">
        {activeItems.map((item) => {
          const isExpanded = expandedItem === item.id;
          const outcome    = outcomes[item.id];
          const isRecorded = !!outcome?.decision;

          return (
            <div key={item.id} className="border border-border/40 rounded-lg overflow-hidden">
              <button
                className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedItem(isExpanded ? null : item.id)}
              >
                <div className="flex items-center gap-2">
                  {isRecorded ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Clock className="w-4 h-4 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium text-foreground">{item.headline}</span>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>

              {isExpanded && (
                <div className="px-3 pb-3 pt-1 border-t border-border/30 space-y-3">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">{t("linkedToSegment")}</span> {getSegmentName(item.segmentId)}
                  </p>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-foreground">{t("boardDirectionLabel")}</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: "resolved",         labelKey: "direction_resolved" },
                        { value: "action-required",  labelKey: "direction_action_required" },
                        { value: "deferred",         labelKey: "direction_deferred" },
                        { value: "monitoring",       labelKey: "direction_monitoring" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          className={`text-xs py-2 px-3 rounded border transition-colors ${
                            outcome?.decision === opt.value
                              ? "border-primary bg-primary/10 text-foreground"
                              : "border-border/50 text-muted-foreground hover:border-border"
                          }`}
                          onClick={() =>
                            setOutcomes((p) => ({
                              ...p,
                              [item.id]: { ...p[item.id], decision: opt.value },
                            }))
                          }
                        >
                          {t(opt.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-foreground">{t("notesOptionalBoard")}</label>
                    <Textarea
                      placeholder={t("boardDirectionNotesPh")}
                      value={outcome?.notes || ""}
                      onChange={(e) =>
                        setOutcomes((p) => ({
                          ...p,
                          [item.id]: { ...p[item.id], notes: e.target.value },
                        }))
                      }
                      className="text-xs h-16 resize-none"
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    disabled={!outcome?.decision}
                    onClick={() => handleRecord(item.id)}
                  >
                    {t("recordDirection")}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground/60 italic">
        {t("unresolvedCarryForward")}
      </p>
    </div>
  );
};
